// HORUS V2 — Activity Log helper
// Insert detections/identifications ke tabel `log_aktivitas` Supabase.
// Pakai dedup window per (kamera, key) supaya tidak spam DB saat scan loop
// detect subjek yang sama berulang kali.

import { supabase } from './supabase';

// Dedup window TTL per jenis (ms)
const DEDUP_FACE    = 60_000;   // 60s untuk wajah (per NIK / unknown bucket)
const DEDUP_PLATE   = 60_000;   // 60s untuk plat
const DEDUP_VEHICLE = 600_000;  // 10 mnt untuk kendaraan (diukur 5.204 baris KENDARAAN dengan hanya 4 subjek unik: car/truck/bus/motorcycle)
const DEDUP_OBJEK   = 30_000;   // 30s untuk objek lain

// In-memory dedup map: key = `${kameraId}|${jenis}|${subjek}`, value = ts
const _seen = new Map();

function shouldLog(key, ttl) {
  const last = _seen.get(key);
  const now = Date.now();
  if (last && now - last < ttl) return false;
  _seen.set(key, now);
  // Garbage collect entri lama biar Map tidak balon
  if (_seen.size > 500) {
    for (const [k, t] of _seen) {
      if (now - t > Math.max(DEDUP_FACE, DEDUP_PLATE) * 2) _seen.delete(k);
    }
  }
  return true;
}

/**
 * Log satu entry deteksi/identifikasi ke Supabase.
 * Fire-and-forget — error di-log ke console, tidak block pemanggil.
 *
 * @param {Object} entry
 * @param {string} entry.jenis              — 'WAJAH_DIKENAL'|'WAJAH_TIDAK_DIKENAL'|'KENDARAAN'|'PLAT'|'OBJEK'
 * @param {string} [entry.subjek]
 * @param {string} [entry.nik]
 * @param {string} [entry.no_plat]
 * @param {string} [entry.status_intelijen]
 * @param {number} [entry.confidence]
 * @param {Object|Array} [entry.bbox]
 * @param {string} [entry.foto_url]
 * @param {Array}  [entry.embedding]
 * @param {Object} [entry.kamera]           — { id, nama, lokasi }
 * @param {Object} [entry.meta]
 */
export function logActivity(entry) {
  if (!entry || !entry.jenis) return;

  // Dedup key per jenis
  let dedupKey, dedupTtl;
  const camId = entry.kamera?.id || 'NO_CAM';
  switch (entry.jenis) {
    case 'WAJAH_DIKENAL':
      dedupKey = `${camId}|face|${entry.nik || entry.subjek || 'unknown'}`;
      dedupTtl = DEDUP_FACE;
      break;
    case 'WAJAH_TIDAK_DIKENAL':
      // Untuk unknown — group by lokasi+jenis (jangan spam tiap frame)
      dedupKey = `${camId}|face-unknown`;
      dedupTtl = DEDUP_FACE;
      break;
    case 'PLAT':
    case 'KENDARAAN':
      dedupKey = `${camId}|plate|${entry.no_plat || entry.subjek || 'unk'}`;
      dedupTtl = entry.jenis === 'PLAT' ? DEDUP_PLATE : DEDUP_VEHICLE;
      break;
    case 'OBJEK':
    default:
      dedupKey = `${camId}|obj|${entry.subjek || 'unknown'}`;
      dedupTtl = DEDUP_OBJEK;
      break;
  }

  if (!shouldLog(dedupKey, dedupTtl)) return;

  const row = {
    jenis:              entry.jenis,
    subjek:             entry.subjek || null,
    nik:                entry.nik || null,
    no_plat:            entry.no_plat || null,
    status_intelijen:   entry.status_intelijen || null,
    confidence:         entry.confidence == null ? null
                          : (entry.confidence > 1 ? entry.confidence / 100 : entry.confidence),
    bbox:               entry.bbox || null,
    foto_url:           entry.foto_url || null,
    embedding:          entry.embedding || null,
    source_kamera_id:   entry.kamera?.id || null,
    source_kamera_nama: entry.kamera?.nama || null,
    lokasi:             entry.kamera?.lokasi || entry.lokasi || null,
    meta:               entry.meta || null,
  };

  // Fire and forget — tidak await, tidak block UI
  supabase.from('log_aktivitas').insert(row).then(({ error }) => {
    if (error) console.warn('[activityLog]', error.message);
  });
}

/**
 * Bulk log dari hasil scan enriched (face + plate + vehicle + objek).
 * Dipanggil dari dispatchAlerts atau NeoVision scan handler.
 */
export function logScanResult(scanResult, kamera) {
  if (!scanResult || !kamera) return;
  const { enrichedFaces = [], enrichedPlates = [], vehicleObjs = [], otherObjs = [] } = scanResult;

  for (const face of enrichedFaces) {
    if (face.match_nik && face.status === 'KNOWN') {
      // Support 2 enrichment shapes:
      //   - aiScanner: face._person = { nama_lengkap, status_intelijen, foto_url, ... }
      //   - NeoVision Kamera Perangkat: face._nama / face._intel / face._kota / face._foto
      const nama  = face._person?.nama_lengkap   || face._nama  || null;
      const intel = face._person?.status_intelijen || face._intel || 'CLEAR';
      const foto  = face._person?.foto_url       || face._foto  || null;
      logActivity({
        jenis: 'WAJAH_DIKENAL',
        subjek: nama || face.match_nik,
        nik: face.match_nik,
        status_intelijen: intel,
        confidence: face.match_score,
        bbox: face.bbox,
        foto_url: foto,
        embedding: face.embedding || null,
        kamera,
        meta: {
          umur: face._umur ?? face.age,
          gender: face._gender || face.gender,
          kota: face._kota,
        },
      });
    } else {
      logActivity({
        jenis: 'WAJAH_TIDAK_DIKENAL',
        subjek: 'Wajah tidak dikenali',
        confidence: face.confidence,
        bbox: face.bbox,
        kamera,
        meta: { gender: face.gender, age: face.age },
      });
    }
  }

  for (const plate of enrichedPlates) {
    // Hanya plat yang terkonfirmasi DB atau berbentuk plat. 3.323 baris PLAT berisi 110 teks unik; bacaan yang tidak
    // berbentuk plat (mis. lencana "ECO" pada bodi) tidak berguna di log.
    if (!(plate.confirmed || /^[A-Z]{1,2}\s?\d{1,4}\s?[A-Z]{1,3}$/.test(plate.plate || ''))) continue;
    logActivity({
      jenis: 'PLAT',
      subjek: plate.plate,
      no_plat: plate.plate,
      status_intelijen: plate.info?.intel || null,
      confidence: plate.confidence,
      bbox: plate.from_bbox || plate.bbox,
      kamera,
      meta: {
        merk: plate.info?.merk_model || null,
        warna: plate.info?.warna || null,
        pemilik: plate.info?.nama_pemilik || null,
      },
    });
  }

  for (const veh of vehicleObjs) {
    logActivity({
      jenis: 'KENDARAAN',
      subjek: veh.label,
      confidence: veh.confidence || veh.score,
      bbox: veh.bbox,
      kamera,
      meta: { label: veh.label },
    });
  }

  for (const obj of otherObjs) {
    if (obj.label === 'person') continue; // skip — sudah di-handle via face
    logActivity({
      jenis: 'OBJEK',
      subjek: obj.label,
      confidence: obj.confidence || obj.score,
      bbox: obj.bbox,
      kamera,
      meta: { label: obj.label },
    });
  }
}
